import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "components/card";
import {
  Patient,
  QuestionnaireAssignment,
  QuestionnaireDetail,
} from "types/patient";
import { patientService } from "services/patientService";
import { fetchWithAuth } from "services/api";
import {
  MdArrowBack,
  MdEdit,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdCheckCircle,
  MdAccessTime,
  MdSend,
  MdVisibility,
  MdAssignment,
  MdDelete,
} from "react-icons/md";
import AssignQuestionnaireModal from "./components/AssignQuestionnaireModal";
import CreateReportModal from "./components/CreateReportModal";

interface Report {
  id: number;
  title: string | null;
  prompt: string | null;
  professional_notes: string | null;
  created_at: string;
  status: string;
  questionnaire_assignments: {
    id: number;
    questionnaire: {
      name: string;
      code: string;
    };
  }[];
}

// Add DeleteConfirmationModal component
const DeleteConfirmationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  assignmentDate: string;
}> = ({ isOpen, onClose, onConfirm, assignmentDate }) => {
  if (!isOpen) return null;

  return (
    <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
        <h2 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
          Confirmar Eliminación
        </h2>
        <p className="mb-6 text-gray-600">
          ¿Está seguro que desea eliminar la asignación del {assignmentDate}?
          Esta acción no se puede deshacer.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className="rounded-lg bg-red-500 px-4 py-2 text-white hover:bg-red-600"
          >
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
};

const PatientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reports, setReports] = useState<Report[]>([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [expandedAssignments, setExpandedAssignments] = useState<Set<number>>(
    new Set()
  );
  const [resendLoading, setResendLoading] = useState<Set<number>>(new Set());
  const [resendSuccess, setResendSuccess] = useState<Set<number>>(new Set());
  const [resendError, setResendError] = useState<Record<number, string>>({});
  const [deleteLoading, setDeleteLoading] = useState<Set<number>>(new Set());
  const [deleteError, setDeleteError] = useState<Record<number, string>>({});

  // Modal states
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<{
    id: number;
    date: string;
  } | null>(null);

  const [showReportModal, setShowReportModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const [patientResponse, reportsResponse] = await Promise.all([
          patientService.getPatient(parseInt(id)),
          fetchWithAuth(`/reports?patient_id=${id}`),
        ]);

        if (patientResponse.success) {
          setPatient(patientResponse.data);
        } else {
          setError(patientResponse.message);
        }

        if (reportsResponse.success) {
          setReports(reportsResponse.data);
        } else {
          setReportsError(reportsResponse.message);
        }
      } catch (err) {
        setError("Failed to fetch patient details");
        setReportsError("Failed to fetch reports");
        console.error(err);
      } finally {
        setLoading(false);
        setReportsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Function to handle assignment submission from the modal
  const handleAssignQuestionnaires = async (
    selectedQuestionnaires: number[],
    deliveryMethods: { email: boolean; whatsapp: boolean }
  ) => {
    try {
      // No need to implement the API call here as it's now handled in the modal component
      console.log(
        "Questionnaires assigned successfully:",
        selectedQuestionnaires
      );

      // Refresh patient data to show the newly assigned questionnaires
      if (id) {
        const response = await patientService.getPatient(parseInt(id));
        if (response.success) {
          setPatient(response.data);
        }
      }

      setShowAssignModal(false);
    } catch (error) {
      console.error("Error refreshing patient data:", error);
    }
  };

  const handleBack = () => {
    navigate("/admin/patients");
  };

  const toggleAssignment = (assignmentId: number) => {
    setExpandedAssignments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(assignmentId)) {
        newSet.delete(assignmentId);
      } else {
        newSet.add(assignmentId);
      }
      return newSet;
    });
  };

  const handleResendQuestionnaires = async (assignmentId: number) => {
    try {
      setResendLoading((prev) => new Set(prev).add(assignmentId));
      setResendError((prev) => ({ ...prev, [assignmentId]: "" }));

      const response = await patientService.resendQuestionnaires(assignmentId);

      if (response.success) {
        setResendSuccess((prev) => new Set(prev).add(assignmentId));
        // Clear success message after 3 seconds
        setTimeout(() => {
          setResendSuccess((prev) => {
            const newSet = new Set(prev);
            newSet.delete(assignmentId);
            return newSet;
          });
        }, 3000);
      } else {
        setResendError((prev) => ({
          ...prev,
          [assignmentId]: response.message,
        }));
      }
    } catch (err) {
      setResendError((prev) => ({
        ...prev,
        [assignmentId]:
          "Error al reenviar los cuestionarios. Intente nuevamente.",
      }));
      console.error(err);
    } finally {
      setResendLoading((prev) => {
        const newSet = new Set(prev);
        newSet.delete(assignmentId);
        return newSet;
      });
    }
  };

  const handleDeleteAssignment = async (assignmentId: number) => {
    try {
      setDeleteLoading((prev) => new Set(prev).add(assignmentId));
      setDeleteError((prev) => ({ ...prev, [assignmentId]: "" }));

      const response = await patientService.deleteQuestionnaireAssignment(
        assignmentId
      );

      if (response.success) {
        // Refresh patient data to remove the deleted assignment
        if (id) {
          const patientResponse = await patientService.getPatient(parseInt(id));
          if (patientResponse.success) {
            setPatient(patientResponse.data);
          }
        }
        setShowDeleteModal(false);
        setAssignmentToDelete(null);
      } else {
        setDeleteError((prev) => ({
          ...prev,
          [assignmentId]: response.message,
        }));
      }
    } catch (err) {
      setDeleteError((prev) => ({
        ...prev,
        [assignmentId]: "Error al eliminar la asignación. Intente nuevamente.",
      }));
      console.error(err);
    } finally {
      setDeleteLoading((prev) => {
        const newSet = new Set(prev);
        newSet.delete(assignmentId);
        return newSet;
      });
    }
  };

  const refreshReports = async () => {
    try {
      setReportsLoading(true);
      const response = await fetchWithAuth(`/reports?patient_id=${id}`);
      if (response.success) {
        setReports(response.data);
      } else {
        setReportsError(response.message);
      }
    } catch (err) {
      setReportsError("Failed to fetch reports");
      console.error(err);
    } finally {
      setReportsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-lg font-semibold text-navy-700 dark:text-white">
          Loading...
        </div>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-lg font-semibold text-red-500">
          {error || "Patient not found"}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 grid h-full gap-5">
      {/* Header with back button and patient name */}
      <div className="flex items-center">
        <button
          onClick={handleBack}
          className="mr-4 flex items-center justify-center rounded-lg p-2 text-navy-700 transition-all hover:bg-gray-100 dark:text-white dark:hover:bg-navy-700"
        >
          <MdArrowBack className="h-6 w-6" />
        </button>
        <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
          {patient.name}
        </h1>
        <button
          onClick={() => setShowAssignModal(true)}
          className="ml-auto flex items-center justify-center gap-1 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600"
        >
          <MdAssignment className="h-5 w-5" />
          <span>Asignar cuestionarios</span>
        </button>
      </div>

      {/* Patient Information Card */}
      <Card extra="w-full p-6">
        <h2 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
          Información del Paciente
        </h2>
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <h3 className="mb-2 text-base font-semibold text-gray-600">
              Información Personal
            </h3>
            <div className="grid gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Nombre</p>
                <p className="text-base text-navy-700 dark:text-white">
                  {patient.name}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Email</p>
                <p className="text-base text-navy-700 dark:text-white">
                  {patient.email}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Teléfono</p>
                <p className="text-base text-navy-700 dark:text-white">
                  {patient.patient_profile.phone}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Dirección</p>
                <p className="text-base text-navy-700 dark:text-white">
                  {patient.patient_profile.address}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Fecha de Nacimiento
                </p>
                <p className="text-base text-navy-700 dark:text-white">
                  {new Date(
                    patient.patient_profile.birth_date
                  ).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Género</p>
                <p className="text-base text-navy-700 dark:text-white">
                  {patient.patient_profile.gender === "male"
                    ? "Masculino"
                    : patient.patient_profile.gender === "female"
                    ? "Femenino"
                    : "Otro"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Ocupación</p>
                <p className="text-base text-navy-700 dark:text-white">
                  {patient.patient_profile.occupation || "No especificado"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Estado Civil
                </p>
                <p className="text-base text-navy-700 dark:text-white">
                  {(() => {
                    switch (patient.patient_profile.marital_status) {
                      case "soltero":
                        return "Soltero";
                      case "casado":
                        return "Casado";
                      case "divorciado":
                        return "Divorciado";
                      case "union convivencial":
                        return "Unión Convivencial";
                      case "viudo":
                        return "Viudo";
                      case "separado":
                        return "Separado";
                      default:
                        return "No especificado";
                    }
                  })()}
                </p>
              </div>
            </div>
          </div>
          <div>
            <h3 className="mb-2 text-base font-semibold text-gray-600">
              Información del Seguro
            </h3>
            <div className="grid gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Compañía de Seguro
                </p>
                <p className="text-base text-navy-700 dark:text-white">
                  {patient.patient_profile.health_insurance_company ||
                    "No especificado"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Plan de Seguro
                </p>
                <p className="text-base text-navy-700 dark:text-white">
                  {patient.patient_profile.health_insurance_plan ||
                    "No especificado"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">
                  ID de Seguro
                </p>
                <p className="text-base text-navy-700 dark:text-white">
                  {patient.patient_profile.health_insurance_id ||
                    "No especificado"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Questionnaires Card */}
      <Card extra="w-full p-6">
        <h2 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
          Cuestionarios Enviados
        </h2>

        {patient.questionnaire_assignments &&
        patient.questionnaire_assignments.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {patient.questionnaire_assignments.map(
              (assignment: QuestionnaireAssignment) => {
                const isExpanded = expandedAssignments.has(assignment.id);
                const hasIncompleteQuestionnaires = assignment.details.some(
                  (detail) => !detail.is_completed
                );
                const assignmentDate = new Date(
                  assignment.created_at
                ).toLocaleDateString();

                return (
                  <div key={assignment.id} className="py-3">
                    <div
                      className="flex cursor-pointer items-center justify-between"
                      onClick={() => toggleAssignment(assignment.id)}
                    >
                      <div className="flex items-center">
                        {isExpanded ? (
                          <MdKeyboardArrowUp className="mr-2 h-6 w-6 text-gray-500" />
                        ) : (
                          <MdKeyboardArrowDown className="mr-2 h-6 w-6 text-gray-500" />
                        )}
                        <div>
                          <p className="font-medium text-navy-700 dark:text-white">
                            Cuestionarios asignados el {assignmentDate}
                          </p>
                          <p className="text-sm text-gray-600">
                            {assignment.details.length} cuestionario(s)
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        {hasIncompleteQuestionnaires && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResendQuestionnaires(assignment.id);
                              }}
                              disabled={resendLoading.has(assignment.id)}
                              className="flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-sm text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {resendLoading.has(assignment.id) ? (
                                <>
                                  <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-white"></div>
                                  <span>Enviando...</span>
                                </>
                              ) : (
                                <>
                                  <MdSend className="h-4 w-4" />
                                  <span>Reenviar cuestionarios</span>
                                </>
                              )}
                            </button>

                            {resendSuccess.has(assignment.id) && (
                              <span className="text-sm text-green-500">
                                ¡Cuestionarios reenviados con éxito!
                              </span>
                            )}

                            {resendError[assignment.id] && (
                              <span className="text-sm text-red-500">
                                {resendError[assignment.id]}
                              </span>
                            )}
                          </>
                        )}

                        {!assignment.is_completed && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setAssignmentToDelete({
                                id: assignment.id,
                                date: assignmentDate,
                              });
                              setShowDeleteModal(true);
                            }}
                            disabled={deleteLoading.has(assignment.id)}
                            className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-500 hover:bg-red-200 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {deleteLoading.has(assignment.id) ? (
                              <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-red-500"></div>
                            ) : (
                              <MdDelete className="h-5 w-5" />
                            )}
                          </button>
                        )}

                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
                          {assignment.is_completed ? (
                            <MdCheckCircle className="h-5 w-5 text-green-500" />
                          ) : (
                            <MdAccessTime className="h-5 w-5 text-amber-500" />
                          )}
                        </div>
                      </div>
                    </div>

                    {deleteError[assignment.id] && (
                      <p className="mt-2 text-sm text-red-500">
                        {deleteError[assignment.id]}
                      </p>
                    )}

                    {isExpanded && (
                      <div className="ml-8 mt-3 rounded-lg bg-gray-50 p-4 dark:bg-navy-800">
                        <div className="space-y-3">
                          {assignment.details.map(
                            (detail: QuestionnaireDetail) => (
                              <div
                                key={detail.id}
                                className={`flex items-center justify-between rounded-lg bg-white p-3 shadow-sm dark:bg-navy-700 ${
                                  detail.is_completed
                                    ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-600"
                                    : ""
                                }`}
                                onClick={() => {
                                  // Only navigate if the questionnaire is completed
                                  if (detail.is_completed) {
                                    navigate(
                                      `/admin/questionnaire-results/${detail.id}`
                                    );
                                  }
                                }}
                              >
                                <div>
                                  <p className="font-medium text-navy-700 dark:text-white">
                                    {detail.questionnaire.name}
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    {detail.questionnaire.code} -{" "}
                                    {detail.questionnaire.description}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {detail.is_completed && (
                                    <button
                                      className="flex items-center gap-1 rounded-lg bg-blue-100 px-2 py-1 text-xs text-blue-700 hover:bg-blue-200"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(
                                          `/admin/questionnaire-results/${detail.id}`
                                        );
                                      }}
                                    >
                                      <MdVisibility className="h-4 w-4" />
                                      <span>Ver resultados</span>
                                    </button>
                                  )}
                                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
                                    {detail.is_completed ? (
                                      <MdCheckCircle className="h-5 w-5 text-green-500" />
                                    ) : (
                                      <MdAccessTime className="h-5 w-5 text-amber-500" />
                                    )}
                                  </div>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
            )}
          </div>
        ) : (
          <p className="text-gray-600">
            No hay cuestionarios asignados a este paciente.
          </p>
        )}
      </Card>

      <Card extra="w-full p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-navy-700 dark:text-white">
            Informes
          </h2>
          <button
            className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-white transition-all hover:bg-brand-600"
            onClick={() => setShowReportModal(true)}
          >
            <MdAssignment className="h-5 w-5" />
            <span>Crear Informe</span>
          </button>
        </div>

        {reportsLoading ? (
          <div className="flex justify-center">
            <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-2 border-brand-500"></div>
          </div>
        ) : reportsError ? (
          <div className="rounded-lg bg-red-100 p-4 text-red-700">
            {reportsError}
          </div>
        ) : reports.length === 0 ? (
          <p className="text-gray-600">
            No hay informes disponibles para este paciente.
          </p>
        ) : (
          <div className="space-y-4">
            {reports.map((report) => (
              <div
                key={report.id}
                className="flex flex-col rounded-lg border border-gray-200 p-4 transition-all hover:bg-gray-50 dark:border-navy-700 dark:hover:bg-navy-800"
              >
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex-grow">
                    <h3 className="font-medium text-navy-700 dark:text-white">
                      {report.title || "Informe sin título"}
                    </h3>
                    <p className="text-sm text-gray-600">
                      Creado el{" "}
                      {new Date(report.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-sm text-white transition-all hover:bg-brand-600"
                      onClick={() => navigate(`/admin/reports/${report.id}`)}
                    >
                      <MdVisibility className="h-4 w-4" />
                      <span>Ver</span>
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {report.questionnaire_assignments.map((assignment) => (
                    <span
                      key={assignment.id}
                      className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700 dark:bg-navy-700 dark:text-gray-300"
                    >
                      {assignment.questionnaire.name} (
                      {assignment.questionnaire.code})
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Add DeleteConfirmationModal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setAssignmentToDelete(null);
        }}
        onConfirm={() => {
          if (assignmentToDelete) {
            handleDeleteAssignment(assignmentToDelete.id);
          }
        }}
        assignmentDate={assignmentToDelete?.date || ""}
      />

      {/* Use the new AssignQuestionnaireModal component */}
      <AssignQuestionnaireModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        patientName={patient.name}
        patientId={patient.id}
        onAssign={handleAssignQuestionnaires}
      />

      <CreateReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        patientId={parseInt(id || "0")}
        onSuccess={refreshReports}
      />
    </div>
  );
};

export default PatientDetail;
